import { normalizeName } from '../shared/naming';
import { roundQuantity } from '../schedule/unit-of-measure';
import { parseSteelGauge, steelGaugeInfo, type SteelGauge } from './steel';

/**
 * O preço do material na obra (Fase 29, etapa 2 — RN-397 e RN-398).
 *
 * Até a Fase 29 o ObraPlan não guardava preço (RN-173). Agora guarda **um preço por material
 * na obra** (D7), pela mesma chave da consolidação: o código de compras, ou a descrição quando
 * não há código. O tijolo é o mesmo tijolo na alvenaria e no encunhamento, e um preço para cada
 * uso daria dois orçamentos para o mesmo material.
 *
 * Três números podem morar na linha, e só um vale:
 *
 * - o **médio** do compras, numa foto com data e período (D5) — o "Atualizar preços" tira outra;
 * - o **provisório**, para quem não tem código ou não teve compra no período;
 * - o **digitado** por cima do médio, que pede motivo (D8).
 *
 * E o **fator de unidade** (D6): o compras vende tijolo por unidade e o item pode pedir milheiro;
 * vende aço por quilo e o item pode contar barra. "1 UN = 1,85 kg" é o fator 1,85 — quantas
 * unidades do compras cabem numa unidade do item.
 */

/** Quatro casas no preço unitário (D17): R$ 0,8537 o tijolo não é R$ 0,85. */
export const PRICE_DECIMALS = 4;

/** Centavos no total — é dinheiro, e não preço de referência. */
export const TOTAL_DECIMALS = 2;

/** O motivo do digitado, como a correção da quantidade o pede (RN-174). */
export const OVERRIDE_REASON_MIN = 3;
export const OVERRIDE_REASON_MAX = 500;

/** O que o compras disse do SKU na foto. Espelha a `preco-medio` (RN-394). */
export type SupplySnapshotStatus = 'OK' | 'SEM_MEDIA' | 'NAO_ENCONTRADO';

/**
 * A foto do compras: o médio de um período, tirada num instante (D5).
 *
 * Guardada inteira — o número, de quantos pontos ele saiu, o menor e o maior —, porque o
 * orçamento é conferido semanas depois, e "R$ 0,85" sem "3 compras e 2 cotações, de 01/10/2025
 * a 13/10/2026" não diz se é para confiar.
 */
export interface SupplyPriceSnapshot {
  readonly status: SupplySnapshotStatus;
  /** Só no `OK`: o médio, por unidade **do compras**. */
  readonly average?: number;
  /** A unidade do produto no compras — a que o fator converte (D6). */
  readonly unit?: string;
  readonly purchases?: number;
  readonly quotations?: number;
  readonly lowest?: number;
  readonly highest?: number;
  readonly lastPurchaseDate?: string;
  readonly lastPurchasePrice?: number;
  /** O período da média, `AAAA-MM-DD`, as duas pontas incluídas (D4). */
  readonly periodFrom: string;
  readonly periodTo: string;
  /** Quando a foto foi tirada (ISO). */
  readonly fetchedAt: string;
}

/** O preço de um material na obra, como o orçamento o guarda (RN-397). */
export interface MaterialPrice {
  /** A chave da consolidação: `codigo:…` ou `descricao:…` (`consolidationKey`). */
  readonly key: string;
  /** O código como o compras o escreve — a chave o guarda em minúsculas. */
  readonly code?: string;
  readonly supply?: SupplyPriceSnapshot;
  /** Unidades do compras numa unidade do item: "1 UN = 1,85 kg" é 1,85 (D6). */
  readonly unitFactor?: number;
  readonly provisionalPrice?: number;
  /** O digitado por cima do médio, sempre com motivo (D8). Os dois existem juntos, ou nenhum. */
  readonly overridePrice?: number;
  readonly overrideReason?: string;
}

/** De onde veio o preço que vale. */
export type MaterialPriceOrigin = 'MEDIO' | 'PROVISORIO' | 'DIGITADO';

/**
 * Por que o preço que vale não é o médio — ou por que não há preço.
 *
 * - `SEM_MEDIA`: o SKU existe e não teve compra no período (RN-393);
 * - `NAO_ENCONTRADO`: o compras não conhece o código, ou ele está inativo (RN-394);
 * - `NAO_BUSCADO`: tem código e ninguém tirou a foto ainda;
 * - `UNIDADE_DIFERENTE`: o compras vende noutra unidade e falta o fator (D6);
 * - `SEM_PRECO`: sem código e sem provisório.
 */
export type MaterialPriceWarning =
  'SEM_MEDIA' | 'NAO_ENCONTRADO' | 'NAO_BUSCADO' | 'UNIDADE_DIFERENTE' | 'SEM_PRECO';

export interface ResolvedMaterialPrice {
  /** Por unidade **do item**. Ausente = o material está sem preço. */
  readonly unitPrice?: number;
  readonly origin?: MaterialPriceOrigin;
  readonly warning?: MaterialPriceWarning;
  /** O fator entrou na conta — a tela escreve "R$ 4,10/kg × 1,85". */
  readonly factorApplied?: number;
  /**
   * De onde veio o fator: o **produto** — confirmado uma vez, vale em todas as obras —, ou a
   * **obra**, que trocou o dela (decidido em 13/10/2026). A obra vence.
   */
  readonly factorSource?: 'PRODUTO' | 'OBRA';
  /** O fator do produto saiu da barra de 12 m da bitola, conferida na importação, ou foi digitado. */
  readonly factorBasis?: FactorBasis;
}

/** Como o fator do produto nasceu: digitado, ou a barra de 12 m da bitola, conferida. */
export type FactorBasis = 'DIGITADO' | 'BITOLA';

/**
 * O fator de unidade de um **produto**, para todas as obras (decidido em 13/10/2026).
 *
 * "1 L = 1/18 UN" é verdade para o desmoldante de 18 L em qualquer obra: digitar o fator de novo
 * em cada uma, e em cada reimportação, era trabalho repetido. Por código e unidade do item — o
 * mesmo código pode ser orçado em kg numa planilha e em barra noutra, e cada par tem o seu.
 */
export interface ProductUnitFactor {
  /** `materialCodeKey(code)`. */
  readonly codeKey: string;
  readonly code: string;
  /** A unidade do item, no jeito único (`canonicalUnit`). */
  readonly itemUnit: string;
  /** A unidade do compras quando o fator foi confirmado — para a tela dizer "1 kg = 0,0528 UN". */
  readonly supplyUnit?: string;
  readonly factor: number;
  readonly basis: FactorBasis;
}

/** A chave do fator do produto: o código e a unidade do item. */
export function productFactorKey(code: string, itemUnit: string): string {
  return `${materialCodeKey(code)}|${canonicalUnit(itemUnit)}`;
}

export const ORIGIN_LABEL: Readonly<Record<MaterialPriceOrigin, string>> = {
  MEDIO: 'Médio do compras',
  PROVISORIO: 'Provisório',
  DIGITADO: 'Digitado',
};

export const WARNING_LABEL: Readonly<Record<MaterialPriceWarning, string>> = {
  SEM_MEDIA: 'sem compra no período',
  NAO_ENCONTRADO: 'código não encontrado no compras',
  NAO_BUSCADO: 'preço ainda não buscado',
  UNIDADE_DIFERENTE: 'unidade diferente — falta o fator',
  SEM_PRECO: 'sem preço',
};

/** A chave de preço de um código — a mesma que a consolidação dá ao item com esse código. */
export function materialCodeKey(code: string): string {
  return `codigo:${normalizeName(code)}`;
}

/** A chave é de material (código ou descrição)? A da função é da mão de obra (etapa 3). */
export function isMaterialPriceKey(key: string): boolean {
  return key.startsWith('codigo:') || key.startsWith('descricao:');
}

/**
 * Os jeitos de escrever a mesma unidade, no jeito único de compará-la.
 *
 * "UN", "UNI", "UND", "UNID" e "UNIDADE" são a mesma unidade — a planilha da obra e o cadastro
 * do compras só a abreviam diferente (confirmado pelo usuário em 13/10/2026: o saco de cimento
 * vinha "UNI" de um lado e "UN" do outro, e ficava sem preço). O expoente também é grafia:
 * "M³" e "M3". O que não está aqui continua diferente — "M" e "M³" não são a mesma coisa.
 */
const MESMA_UNIDADE: Readonly<Record<string, string>> = {
  uni: 'un',
  und: 'un',
  unid: 'un',
  unidade: 'un',
  unidades: 'un',
};

/** A unidade no jeito único: sem caixa, espaço nem ponto; o expoente como dígito. */
export function canonicalUnit(unit: string): string {
  const texto = normalizeName(unit).replace(/³/g, '3').replace(/²/g, '2').replace(/[.\s]/g, '');
  return MESMA_UNIDADE[texto] ?? texto;
}

/**
 * As duas unidades dizem a mesma coisa?
 *
 * Pelo jeito único (`canonicalUnit`): "UN" e "UNI" são a mesma; "UN" e "KG", não. A unidade que
 * falta de um dos lados não pede fator: não há o que converter, e travar o preço por isso
 * puniria o item que veio sem unidade da planilha.
 */
export function sameUnit(supplyUnit: string | undefined, itemUnit: string | undefined): boolean {
  if (supplyUnit === undefined || supplyUnit.trim() === '') return true;
  if (itemUnit === undefined || itemUnit.trim() === '') return true;
  return canonicalUnit(supplyUnit) === canonicalUnit(itemUnit);
}

/** O comprimento da barra de aço que o compras vende (D6, confirmado em 13/10/2026). */
export const STEEL_BAR_LENGTH_M = 12;

/** As unidades em que o compras vende o vergalhão por barra. */
const POR_BARRA: ReadonlySet<string> = new Set(['un', 'br', 'barra', 'barras', 'vara', 'varas']);

/**
 * O fator da barra de 12 m da bitola: quantas barras há num quilo.
 *
 * O orçamento mede o aço em kg (RN-366) e o compras o vende por barra: o CA-50 16 mm custa
 * R$ 98,80 a barra, que pesa 1,578 kg/m × 12 m = 18,94 kg — R$ 5,22 o quilo. Só vale para o item
 * em kg, com bitola conhecida, contra o compras em barra; fora disso, `undefined`.
 *
 * É **sugestão**, e não regra: a importação a mostra para alguém conferir e confirmar, uma vez
 * por produto (decidido em 13/10/2026) — a barra pode ser de outro comprimento, e o fator que
 * entra no preço é o confirmado (`ProductUnitFactor`).
 */
export function steelBarFactor(
  item: {
    readonly unit?: string;
    readonly steelGauge?: SteelGauge;
    readonly description?: string;
  },
  supplyUnit: string | undefined,
): number | undefined {
  if (item.unit === undefined || canonicalUnit(item.unit) !== 'kg') return undefined;
  if (supplyUnit === undefined || !POR_BARRA.has(canonicalUnit(supplyUnit))) return undefined;
  const bitola =
    item.steelGauge ??
    (item.description === undefined ? undefined : parseSteelGauge(item.description));
  if (bitola === undefined) return undefined;
  return 1 / (steelGaugeInfo(bitola).kgPerMeter * STEEL_BAR_LENGTH_M);
}

export const roundPrice = (value: number): number => roundQuantity(value, PRICE_DECIMALS);

/**
 * O preço que vale (RN-398).
 *
 * 1. O **digitado** vence tudo: é uma decisão de alguém, com motivo, e uma foto nova do compras
 *    não a desfaz calada. Voltar ao médio é tirar o digitado.
 * 2. Com média no período, vale o **médio** — vezes o fator quando a unidade é outra.
 * 3. Sem médio que valha, vale o **provisório**, com o aviso de por que não é o médio (D8).
 *    Unidade diferente sem fator é médio que não vale (D6): o preço do quilo no lugar do da
 *    barra erraria o orçamento sem que nenhum número parecesse estranho. Sem provisório, o
 *    material fica **sem preço**, com o aviso.
 */
export function resolveMaterialPrice(
  price: MaterialPrice | undefined,
  item: {
    readonly code?: string;
    readonly unit?: string;
    readonly steelGauge?: SteelGauge;
    readonly description?: string;
  },
  /** O fator do produto para o código e a unidade do item, quando alguém já o confirmou. */
  productFactor?: ProductUnitFactor,
): ResolvedMaterialPrice {
  if (price?.overridePrice !== undefined) {
    return { unitPrice: price.overridePrice, origin: 'DIGITADO' };
  }

  const supply = price?.supply;
  let motivo: MaterialPriceWarning | undefined;
  if (supply?.status === 'OK' && supply.average !== undefined) {
    if (sameUnit(supply.unit, item.unit)) return { unitPrice: supply.average, origin: 'MEDIO' };
    // O da obra vence o do produto: é a obra que comprou a barra de outro comprimento.
    if (price?.unitFactor !== undefined) {
      return {
        unitPrice: roundPrice(supply.average * price.unitFactor),
        origin: 'MEDIO',
        factorApplied: price.unitFactor,
        factorSource: 'OBRA',
      };
    }
    if (productFactor !== undefined) {
      return {
        unitPrice: roundPrice(supply.average * productFactor.factor),
        origin: 'MEDIO',
        factorApplied: productFactor.factor,
        factorSource: 'PRODUTO',
        factorBasis: productFactor.basis,
      };
    }
    motivo = 'UNIDADE_DIFERENTE';
  } else {
    motivo =
      supply?.status === 'SEM_MEDIA'
        ? 'SEM_MEDIA'
        : supply?.status === 'NAO_ENCONTRADO'
          ? 'NAO_ENCONTRADO'
          : item.code !== undefined
            ? 'NAO_BUSCADO'
            : undefined;
  }

  if (price?.provisionalPrice !== undefined) {
    return {
      unitPrice: price.provisionalPrice,
      origin: 'PROVISORIO',
      ...(motivo === undefined ? {} : { warning: motivo }),
    };
  }
  return { warning: motivo ?? 'SEM_PRECO' };
}

/**
 * Onde cai o preço que alguém digita na linha (D8).
 *
 * Por cima de um médio que vale, é o **digitado**, e pede motivo; onde já há digitado, troca o
 * digitado. Onde não há médio que valha — sem código, sem compra no período, unidade sem fator —,
 * é o **provisório**, e não pede: não há número do compras sendo contrariado.
 */
export function typedPriceField(resolved: ResolvedMaterialPrice): 'override' | 'provisional' {
  return resolved.origin === 'MEDIO' || resolved.origin === 'DIGITADO' ? 'override' : 'provisional';
}

/** Quantidade × preço, em centavos. */
export function materialTotal(quantity: number, unitPrice: number): number {
  return roundQuantity(quantity * unitPrice, TOTAL_DECIMALS);
}

/** A linha não guarda mais nada — a gravação a tira, em vez de guardar um registro vazio. */
export function isEmptyMaterialPrice(price: MaterialPrice): boolean {
  return (
    price.supply === undefined &&
    price.unitFactor === undefined &&
    price.provisionalPrice === undefined &&
    price.overridePrice === undefined
  );
}

/**
 * O período do orçamento: o da foto mais recente (D4).
 *
 * O período não mora numa tabela própria — o "Atualizar preços" tira a foto de todos os códigos
 * com o período escolhido, e é a foto que o guarda. Ausente, a tela propõe os doze meses até hoje.
 */
export function currentPricePeriod(
  prices: readonly MaterialPrice[],
): { readonly from: string; readonly to: string } | undefined {
  let maisRecente: SupplyPriceSnapshot | undefined;
  for (const price of prices) {
    const foto = price.supply;
    if (foto === undefined) continue;
    if (maisRecente === undefined || foto.fetchedAt > maisRecente.fetchedAt) maisRecente = foto;
  }
  return maisRecente === undefined
    ? undefined
    : { from: maisRecente.periodFrom, to: maisRecente.periodTo };
}

/** Um material da consolidação, com o preço que vale e o total. */
export interface PricedMaterial<T> {
  readonly item: T;
  readonly price?: MaterialPrice;
  readonly resolved: ResolvedMaterialPrice;
  /** Ausente = sem preço; o total da obra não o conta, e diz quantos ficaram de fora. */
  readonly total?: number;
}

export interface MaterialBudgetSummary {
  readonly total: number;
  readonly byOrigin: Readonly<Record<MaterialPriceOrigin, number>>;
  /** Quantos materiais ficaram sem preço — o total da obra está incompleto enquanto houver. */
  readonly withoutPrice: number;
}

/**
 * Põe preço na lista consolidada, linha a linha, e soma (D15).
 *
 * Genérico no item para servir à consolidação da obra e à de uma tipologia sem copiar a conta.
 */
export function priceMaterials<
  T extends {
    readonly key: string;
    readonly code?: string;
    readonly unit?: string;
    readonly description?: string;
    readonly steelGauge?: SteelGauge;
    readonly total: number;
  },
>(
  items: readonly T[],
  prices: ReadonlyMap<string, MaterialPrice>,
  /** Os fatores dos produtos, por `productFactorKey` — confirmados uma vez, valem em toda obra. */
  productFactors: ReadonlyMap<string, ProductUnitFactor> = new Map(),
): { readonly rows: readonly PricedMaterial<T>[]; readonly summary: MaterialBudgetSummary } {
  const byOrigin: Record<MaterialPriceOrigin, number> = { MEDIO: 0, PROVISORIO: 0, DIGITADO: 0 };
  let total = 0;
  let withoutPrice = 0;
  const rows = items.map((item): PricedMaterial<T> => {
    const price = prices.get(item.key);
    const doProduto =
      item.code === undefined || item.unit === undefined
        ? undefined
        : productFactors.get(productFactorKey(item.code, item.unit));
    const resolved = resolveMaterialPrice(price, item, doProduto);
    if (resolved.unitPrice === undefined || resolved.origin === undefined) {
      withoutPrice += 1;
      return { item, resolved, ...(price === undefined ? {} : { price }) };
    }
    const linha = materialTotal(item.total, resolved.unitPrice);
    byOrigin[resolved.origin] += 1;
    total += linha;
    return { item, resolved, total: linha, ...(price === undefined ? {} : { price }) };
  });
  return {
    rows,
    summary: { total: roundQuantity(total, TOTAL_DECIMALS), byOrigin, withoutPrice },
  };
}
